import { stamp } from '../bookings/booking-model';
import {
  type Consultation,
  type Order,
  type PrescriptionRequest,
  type UploadType,
} from './prescriptions-api';

/**
 * The Prescriptions page's decisions, made once and pinned by tests.
 *
 * Two real sources become one list of "your medicines":
 *
 * - a private prescription: an online consultation and the order it was paid
 *   on (statuses from the consultation, then the order);
 * - a request to a pharmacy: a repeat or an uploaded prescription (statuses
 *   set by the pharmacy from its dashboard).
 *
 * The frame's NHS rows (nominated pharmacy, NHS number, GP, "4 of 6 left",
 * £9.90 NHS charge, "next due", "usually 2 working days", "How to take it")
 * have no record behind them and are never drawn from here.
 */

export interface Tone {
  readonly bg: string;
  readonly fg: string;
}

const VIOLET: Tone = { bg: '#f0ecfa', fg: '#4a22bd' };
const AMBER: Tone = { bg: '#fdf2e4', fg: '#8c4a06' };
const GREEN: Tone = { bg: '#ddf2e7', fg: '#0b5a4a' };
const RED: Tone = { bg: '#fbe3e3', fg: '#8c1919' };
const TEAL: Tone = { bg: '#e7f6fa', fg: '#0d5d73' };
const GREY: Tone = { bg: '#f7f5fc', fg: '#55506b' };
const YELLOW: Tone = { bg: '#fff6dc', fg: '#7a4a00' };

export type MedicineType = 'private' | 'upload' | 'repeat';
export type StripIcon =
  'clock' | 'store' | 'redo' | 'mail' | 'rx' | 'stop' | 'chat' | 'card' | 'check' | 'truck';

export const TYPE_LABEL: Readonly<Record<MedicineType, { label: string } & Tone>> = {
  // The frame's own type tones.
  private: { label: 'PRIVATE', bg: '#fdeef0', fg: '#c0396a' },
  upload: { label: 'UPLOADED', bg: '#fff1d6', fg: '#b86e00' },
  repeat: { label: 'REPEAT REQUEST', bg: '#e7f2fb', fg: '#005eb8' },
};

export const UPLOAD_TYPE_LABEL: Readonly<Record<UploadType, string>> = {
  PRIVATE: 'Private prescription',
  NHS_PAPER: 'Paper NHS prescription',
  VETERINARY: 'Veterinary prescription',
};

export interface Strip extends Tone {
  readonly text: string;
  readonly icon: StripIcon;
}

export interface Step {
  readonly label: string;
  readonly sub: string;
  readonly state: 'done' | 'current' | 'todo' | 'bad';
}

/** One row of "Your medicines". */
export interface Medicine {
  /** `c:` a consultation, `r:` a request — the route says which. */
  readonly key: string;
  readonly href: string;
  readonly type: MedicineType;
  readonly name: string;
  readonly meta: string;
  readonly active: boolean;
  readonly at: number;
  readonly badge: { label: string } & Tone;
  readonly strip: Strip;
  readonly storeName: string;
  /** A collection code exists for this item now — the order's handover, or the request's own. */
  readonly ready: boolean;
}

export const prescriptionsPath = '/prescriptions';
export const medicineHref = (kind: 'consultation' | 'request', id: string): string =>
  `${prescriptionsPath}/${encodeURIComponent(id)}?kind=${kind}`;

const date = (iso: string): string =>
  new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(new Date(iso));

// -- A private prescription: a consultation and its order ---------------------------------

export type PrivateState =
  | 'pay'
  | 'review'
  | 'info'
  | 'approved'
  | 'dispensing'
  | 'ready'
  | 'sent'
  | 'complete'
  | 'declined'
  | 'cancelled'
  | 'expired';

export function privateState(c: Consultation, order: Order | undefined, now = Date.now()): PrivateState {
  switch (c.status) {
    case 'SUBMITTED':
      return Date.parse(c.expiresAt) > now ? 'pay' : 'expired';
    case 'IN_ORDER':
      return order?.status === 'CANCELLED' ? 'cancelled' : 'review';
    case 'MORE_INFO':
      return 'info';
    case 'DECLINED':
      return 'declined';
    case 'EXPIRED':
      return 'expired';
    case 'APPROVED':
      switch (order?.status) {
        case 'READY_FOR_COLLECTION':
          return 'ready';
        case 'OUT_FOR_DELIVERY':
          return 'sent';
        case 'COMPLETED':
          return 'complete';
        case 'CANCELLED':
          return 'cancelled';
        case 'CONFIRMED':
        case 'PREPARING':
          return 'dispensing';
        default:
          return 'approved';
      }
    default:
      return 'expired';
  }
}

const PRIVATE_BADGE: Readonly<Record<PrivateState, { label: string } & Tone>> = {
  pay: { label: 'NOT PAID YET', ...VIOLET },
  review: { label: 'PRESCRIBER REVIEW', ...TEAL },
  info: { label: 'REPLY NEEDED', ...AMBER },
  approved: { label: 'APPROVED', ...GREEN },
  dispensing: { label: 'DISPENSING', ...AMBER },
  ready: { label: 'READY TO COLLECT', ...GREEN },
  sent: { label: 'ON ITS WAY', ...VIOLET },
  complete: { label: 'COMPLETED', ...GREEN },
  declined: { label: 'NOT APPROVED', ...RED },
  cancelled: { label: 'CANCELLED', ...GREY },
  expired: { label: 'EXPIRED', ...GREY },
};

const PRIVATE_ACTIVE: ReadonlySet<PrivateState> = new Set([
  'pay',
  'review',
  'info',
  'approved',
  'dispensing',
  'ready',
  'sent',
]);

function privateStrip(state: PrivateState, store: string, c: Consultation, order: Order | undefined): Strip {
  switch (state) {
    case 'pay':
      // Owner decision D9: pay first, then a prescriber reviews.
      return {
        text: 'Pay at checkout. A prescriber reviews your answers after you pay',
        icon: 'card',
        ...VIOLET,
      };
    case 'review':
      return order?.payment.status === 'PAID'
        ? { text: 'Paid. A prescriber is reviewing your answers', icon: 'clock', ...TEAL }
        : { text: 'Waiting for payment, then a prescriber reviews it', icon: 'card', ...TEAL };
    case 'info':
      return { text: 'Your prescriber has a question for you', icon: 'chat', ...AMBER };
    case 'approved':
      return { text: `Approved. The order goes to ${store}`, icon: 'check', ...GREEN };
    case 'dispensing':
      return { text: `${store} is preparing it`, icon: 'rx', ...AMBER };
    case 'ready':
      return { text: `Ready at ${store}`, icon: 'store', ...GREEN };
    case 'sent':
      return { text: 'On its way to you', icon: 'truck', ...VIOLET };
    case 'complete':
      return {
        text: order !== undefined ? `Completed · order ${order.reference}` : 'Completed',
        icon: 'check',
        ...GREY,
      };
    case 'declined':
      return {
        text: c.decision?.reason ?? 'The prescriber did not approve it',
        icon: 'stop',
        ...GREY,
      };
    case 'cancelled':
      return { text: 'The order was cancelled', icon: 'stop', ...GREY };
    default:
      return { text: 'These answers expired before an order was paid', icon: 'stop', ...GREY };
  }
}

export function privateMedicine(
  c: Consultation,
  order: Order | undefined,
  store: string,
  now = Date.now(),
): Medicine {
  const state = privateState(c, order, now);
  return {
    key: `c:${c.id}`,
    href: medicineHref('consultation', c.id),
    type: 'private',
    name: c.itemName,
    meta: `Online consultation ${c.reference}${order !== undefined ? ` · order ${order.reference}` : ''}`,
    active: PRIVATE_ACTIVE.has(state),
    at: Date.parse(c.submittedAt),
    badge: PRIVATE_BADGE[state],
    strip: privateStrip(state, store, c, order),
    storeName: store,
    ready: state === 'ready',
  };
}

/** The frame's four private steps, from the consultation and the order. */
export function privateSteps(
  c: Consultation,
  order: Order | undefined,
  store: string,
  now = Date.now(),
): Step[] {
  const state = privateState(c, order, now);
  const delivery = order?.fulfilment === 'DELIVERY';
  type S = Step['state'];
  const D: S = 'done';
  const C: S = 'current';
  const T: S = 'todo';
  const B: S = 'bad';
  const table: Readonly<Record<PrivateState, readonly [S, S, S, S]>> = {
    pay: [D, C, T, T],
    review: [D, C, T, T],
    info: [D, C, T, T],
    approved: [D, D, C, T],
    dispensing: [D, D, C, T],
    sent: [D, D, D, D],
    ready: [D, D, D, D],
    complete: [D, D, D, D],
    declined: [D, B, T, T],
    cancelled: c.decision?.outcome === 'APPROVED' ? [D, D, B, T] : [D, B, T, T],
    expired: [D, B, T, T],
  };
  const row = table[state];
  const at = (n: 0 | 1 | 2 | 3): S => row[n];
  return [
    { label: 'Online consultation', sub: `Answers sent ${stamp(c.submittedAt)}`, state: at(0) },
    {
      label: 'Prescriber approved',
      sub:
        state === 'declined'
          ? 'Not approved'
          : state === 'expired'
            ? 'Expired before payment'
            : c.decision?.outcome === 'APPROVED'
              ? `Approved ${stamp(c.decision.at)}`
              : state === 'info'
                ? 'Waiting for your reply'
                : 'After you pay',
      state: at(1),
    },
    { label: 'Dispensing', sub: `By ${store}`, state: at(2) },
    {
      label: delivery ? 'Sent' : 'Ready',
      sub: delivery ? 'Delivered to you' : 'Ready to collect',
      state: at(3),
    },
  ];
}

// -- A request to a pharmacy: a repeat or an upload ----------------------------------------

const REQUEST_BADGE: Readonly<Record<PrescriptionRequest['status'], { label: string } & Tone>> = {
  REQUESTED: { label: 'SENT', ...VIOLET },
  WITH_GP: { label: 'WITH YOUR GP', ...TEAL },
  CHECKING: { label: 'CHECKING', ...VIOLET },
  DISPENSING: { label: 'DISPENSING', ...AMBER },
  READY: { label: 'READY TO COLLECT', ...GREEN },
  COMPLETED: { label: 'COLLECTED', ...GREEN },
  DECLINED: { label: 'NOT SUPPLIED', ...RED },
  CANCELLED: { label: 'CANCELLED', ...GREY },
};

const REQUEST_ACTIVE: ReadonlySet<PrescriptionRequest['status']> = new Set([
  'REQUESTED',
  'WITH_GP',
  'CHECKING',
  'DISPENSING',
  'READY',
]);

/** When the request last reached a status, from its own history. */
function reachedAt(r: PrescriptionRequest, status: PrescriptionRequest['status']): string | undefined {
  return [...r.history].reverse().find((h) => h.status === status)?.at;
}

export function requestStrip(r: PrescriptionRequest): Strip {
  const store = r.storefront.name;
  switch (r.status) {
    case 'REQUESTED':
      return {
        text: `Sent to ${store} ${date(r.createdAt)} · waiting for them to start`,
        icon: 'clock',
        ...VIOLET,
      };
    case 'WITH_GP':
      return {
        text: `${store} sent it to your GP ${date(reachedAt(r, 'WITH_GP') ?? r.updatedAt)}`,
        icon: 'clock',
        ...TEAL,
      };
    case 'CHECKING':
      return {
        text: 'The pharmacist is checking it. Hand in or post the original paper copy',
        icon: 'mail',
        ...YELLOW,
      };
    case 'DISPENSING':
      return { text: `${store} is preparing it`, icon: 'rx', ...AMBER };
    case 'READY':
      return { text: `Ready at ${store}`, icon: 'store', ...GREEN };
    case 'COMPLETED':
      return { text: `Collected ${date(reachedAt(r, 'COMPLETED') ?? r.updatedAt)}`, icon: 'check', ...GREY };
    case 'DECLINED':
      return { text: r.declineReason ?? `${store} could not supply it`, icon: 'stop', ...GREY };
    default:
      return {
        text: `You cancelled it ${date(reachedAt(r, 'CANCELLED') ?? r.updatedAt)}`,
        icon: 'stop',
        ...GREY,
      };
  }
}

export function requestName(r: PrescriptionRequest): string {
  if (r.kind === 'UPLOAD') return UPLOAD_TYPE_LABEL[r.uploadType ?? 'PRIVATE'];
  const [first, ...rest] = r.items;
  if (first === undefined) return 'Repeat request';
  return rest.length === 0 ? first.name : `${first.name} and ${String(rest.length)} more`;
}

export function requestMeta(r: PrescriptionRequest): string {
  if (r.kind === 'UPLOAD') return `${r.documentName ?? 'Your upload'} · ${r.reference}`;
  const quantity = r.items.length === 1 ? r.items[0]?.quantity : undefined;
  return `${quantity !== undefined ? `${quantity} · ` : ''}${r.reference}`;
}

export function requestMedicine(r: PrescriptionRequest): Medicine {
  return {
    key: `r:${r.id}`,
    href: medicineHref('request', r.id),
    type: r.kind === 'UPLOAD' ? 'upload' : 'repeat',
    name: requestName(r),
    meta: requestMeta(r),
    active: REQUEST_ACTIVE.has(r.status),
    at: Date.parse(r.createdAt),
    badge: REQUEST_BADGE[r.status],
    strip: requestStrip(r),
    storeName: r.storefront.name,
    ready: r.status === 'READY' && r.collectionCode !== undefined,
  };
}

/** The frame's four steps for a repeat or an upload, from the request's own history. */
export function requestSteps(r: PrescriptionRequest): Step[] {
  const store = r.storefront.name;
  const order: PrescriptionRequest['status'][] =
    r.kind === 'REPEAT'
      ? ['REQUESTED', 'WITH_GP', 'DISPENSING', 'READY']
      : ['REQUESTED', 'CHECKING', 'DISPENSING', 'READY'];
  // The step in hand: REQUESTED waits on step 2 (the GP, or the check); READY and COMPLETED finish all four.
  const current = (status: PrescriptionRequest['status']): number =>
    status === 'READY' || status === 'COMPLETED' ? 4 : Math.max(1, order.indexOf(status));
  const stopped = r.status === 'DECLINED' || r.status === 'CANCELLED';
  const lastGood = [...r.history].reverse().find((h) => h.status !== 'DECLINED' && h.status !== 'CANCELLED');
  const cur = current(stopped ? (lastGood?.status ?? 'REQUESTED') : r.status);
  const state = (n: number): Step['state'] =>
    n < cur ? 'done' : n === cur ? (stopped ? 'bad' : 'current') : 'todo';
  const when = (status: PrescriptionRequest['status']): string | undefined => {
    const at = reachedAt(r, status);
    return at === undefined ? undefined : stamp(at);
  };
  const repeat = r.kind === 'REPEAT';
  const sentBy = when('WITH_GP');
  return [
    {
      label: repeat ? 'Requested' : 'Uploaded',
      sub: `Sent to ${store} ${stamp(r.createdAt)}`,
      state: state(0),
    },
    repeat
      ? {
          label: 'With your GP',
          sub: sentBy !== undefined ? `Sent by ${store} ${sentBy}` : `${store} contacts your GP if needed`,
          state: state(1),
        }
      : {
          label: 'Pharmacist check',
          sub: when('CHECKING') !== undefined ? 'Hand in or post the original' : 'Checked by the pharmacist',
          state: state(1),
        },
    { label: 'Pharmacy dispensing', sub: `By ${store}`, state: state(2) },
    {
      label: 'Ready',
      sub: r.status === 'COMPLETED' ? `Collected ${String(when('COMPLETED'))}` : 'Ready to collect',
      state: state(3),
    },
  ];
}

/** Active first by newest; history newest first. */
export function splitMedicines(all: readonly Medicine[]): { active: Medicine[]; history: Medicine[] } {
  const sorted = [...all].sort((a, b) => b.at - a.at);
  return { active: sorted.filter((m) => m.active), history: sorted.filter((m) => !m.active) };
}
